import { useState, useEffect } from 'react';
import { fiadosService, configService } from '../../services';
import { formatCurrency, formatDateTime } from '../../utils/format';

export default function Fiados() {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clienteAberto, setClienteAberto] = useState(null); // { id, nome, telefone }
  const [detalheLista, setDetalheLista] = useState([]);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [formas, setFormas] = useState({}); // { [fiadoId]: 'dinheiro' }
  const [bandeiras, setBandeiras] = useState({}); // { [fiadoId]: '' }
  const [operadoras, setOperadoras] = useState([]);
  const [valores, setValores] = useState({}); // { [fiadoId]: '' }
  const [processando, setProcessando] = useState({}); // { [fiadoId]: bool }

  const carregar = () => {
    setLoading(true);
    fiadosService.listar()
      .then(r => setClientes(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { carregar(); }, []);

  useEffect(() => {
    configService.taxasCartao()
      .then(({ data }) => setOperadoras(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const abrirCliente = async (c) => {
    setClienteAberto(c);
    setCarregandoDetalhe(true);
    try {
      const r = await fiadosService.porCliente(c.cliente_id);
      setDetalheLista(r.data);
      const novasFormas = {};
      const novosValores = {};
      r.data.forEach(f => { novasFormas[f.id] = 'dinheiro'; novosValores[f.id] = ''; });
      setFormas(novasFormas);
      setBandeiras({});
      setValores(novosValores);
    } catch {}
    finally { setCarregandoDetalhe(false); }
  };

  const fecharModal = () => {
    setClienteAberto(null);
    setDetalheLista([]);
  };

  const pagarFiado = async (fiadoId, saldo) => {
    const valor = parseFloat(valores[fiadoId]);
    if (!valor || valor <= 0) { alert('Informe o valor do pagamento'); return; }
    if (valor > parseFloat(saldo)) { alert('Valor maior que o saldo devedor'); return; }
    setProcessando(prev => ({ ...prev, [fiadoId]: true }));
    try {
      const forma = formas[fiadoId];
      const bandeira = (forma === 'debito' || forma === 'credito') ? (bandeiras[fiadoId] || null) : null;
      await fiadosService.pagar(fiadoId, { valor, forma, bandeira });
      carregar();
      // Recarrega detalhe do cliente
      const r = await fiadosService.porCliente(clienteAberto.cliente_id);
      setDetalheLista(r.data);
      setValores(prev => ({ ...prev, [fiadoId]: '' }));
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao registrar pagamento');
    } finally {
      setProcessando(prev => ({ ...prev, [fiadoId]: false }));
    }
  };

  const quitarTudo = async (fiadoId, saldo) => {
    setValores(prev => ({ ...prev, [fiadoId]: String(parseFloat(saldo).toFixed(2)) }));
  };

  const totalGeralDevendo = clientes.reduce((s, c) => s + parseFloat(c.total_devendo), 0);

  if (loading) return <div className="loading"><div className="spinner"></div>Carregando...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1>📒 Fiados</h1>
        {clientes.length > 0 && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, color: 'var(--gray3)' }}>{clientes.length} cliente(s) com pendência</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--danger)' }}>{formatCurrency(totalGeralDevendo)}</div>
          </div>
        )}
      </div>

      <div className="table-container card">
        <table>
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Telefone</th>
              <th>Pendências</th>
              <th>Total Devendo</th>
              <th>Último Registro</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {clientes.map(c => (
              <tr key={c.cliente_id}>
                <td><strong>{c.nome}</strong></td>
                <td>{c.telefone || '—'}</td>
                <td style={{ textAlign: 'center' }}>{c.total_pendencias}</td>
                <td style={{ color: 'var(--danger)', fontWeight: 600 }}>{formatCurrency(c.total_devendo)}</td>
                <td>{formatDateTime(c.ultimo_registro)}</td>
                <td>
                  <button className="btn btn-sm btn-primary" onClick={() => abrirCliente(c)}>
                    Ver / Receber
                  </button>
                </td>
              </tr>
            ))}
            {clientes.length === 0 && (
              <tr><td colSpan="6" className="no-data">Nenhum fiado pendente</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {clienteAberto && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h2 style={{ margin: 0 }}>📒 {clienteAberto.nome}</h2>
                {clienteAberto.telefone && (
                  <p style={{ margin: '4px 0 0', color: 'var(--gray3)', fontSize: 14 }}>📞 {clienteAberto.telefone}</p>
                )}
              </div>
              <button className="btn btn-outline btn-sm" onClick={fecharModal}>✕ Fechar</button>
            </div>

            {carregandoDetalhe ? (
              <div className="loading"><div className="spinner"></div></div>
            ) : detalheLista.length === 0 ? (
              <p style={{ color: 'var(--gray3)', textAlign: 'center', padding: 24 }}>Nenhum fiado pendente</p>
            ) : (
              detalheLista.map(f => (
                <div key={f.id} className="card" style={{ marginBottom: 12, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div>
                      <strong>Pedido #{f.pedido_numero}</strong>
                      <span style={{
                        marginLeft: 8, fontSize: 11, padding: '2px 8px', borderRadius: 20,
                        background: f.status === 'pago' ? '#e8f5e9' : f.status === 'parcial' ? '#fff3e0' : '#fce4ec',
                        color: f.status === 'pago' ? '#388e3c' : f.status === 'parcial' ? '#f57c00' : '#c62828',
                        fontWeight: 600, textTransform: 'uppercase'
                      }}>
                        {f.status === 'pago' ? 'Pago' : f.status === 'parcial' ? 'Parcial' : 'Pendente'}
                      </span>
                      <p style={{ fontSize: 12, color: 'var(--gray3)', margin: '2px 0 0' }}>{formatDateTime(f.created_at)}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, fontSize: 13 }}>Total: {formatCurrency(f.valor)}</p>
                      <p style={{ margin: 0, color: 'var(--danger)', fontWeight: 700, fontSize: 16 }}>
                        Saldo: {formatCurrency(f.saldo)}
                      </p>
                    </div>
                  </div>

                  {f.status !== 'pago' && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Valor a receber"
                        value={valores[f.id] || ''}
                        onChange={e => setValores(prev => ({ ...prev, [f.id]: e.target.value }))}
                        style={{ flex: 1, minWidth: 120, padding: '6px 10px', border: '2px solid var(--gray)', borderRadius: 'var(--radius)' }}
                      />
                      <select
                        value={formas[f.id] || 'dinheiro'}
                        onChange={e => setFormas(prev => ({ ...prev, [f.id]: e.target.value }))}
                        style={{ padding: '6px 10px', border: '2px solid var(--gray)', borderRadius: 'var(--radius)' }}
                      >
                        <option value="dinheiro">💵 Dinheiro</option>
                        <option value="pix">⚡ PIX</option>
                        <option value="debito">💳 Débito</option>
                        <option value="credito">💳 Crédito</option>
                      </select>
                      {(formas[f.id] === 'debito' || formas[f.id] === 'credito') && (
                        <select
                          value={bandeiras[f.id] || ''}
                          onChange={e => setBandeiras(prev => ({ ...prev, [f.id]: e.target.value }))}
                          style={{ padding: '6px 10px', border: '2px solid var(--gray)', borderRadius: 'var(--radius)' }}
                        >
                          <option value="">Operadora...</option>
                          {operadoras.map(o => (
                            <option key={o.id} value={o.nome}>{o.nome}</option>
                          ))}
                        </select>
                      )}
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => quitarTudo(f.id, f.saldo)}
                        title="Preencher com o saldo total"
                      >
                        Quitar tudo
                      </button>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => pagarFiado(f.id, f.saldo)}
                        disabled={processando[f.id]}
                      >
                        {processando[f.id] ? '...' : '✅ Receber'}
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
